import { toast } from "sonner";
import { useState } from "react";
import { ShieldCheck, Users, ScrollText, MessageSquare, UserPlus, Trash2, Building2, CreditCard, KeyRound, AlertTriangle, Pencil, Plus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, useFetch, Spinner, StatCard } from "@/components/portal/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

function fmt(t) { return t ? new Date(t).toLocaleString("en-ZA", { timeStyle: "short", dateStyle: "short" }) : "—"; }
function apiErr(e, fb) { return e?.response?.data?.detail || fb; }

/* ----------------------------- SMS Settings ----------------------------- */
function SmsSettings({ isSuperAdmin }) {
  const { data, loading, setData } = useFetch(() => api.get("/admin/sms-settings").then((r) => r.data));
  const [f, setF] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  if (loading) return <Spinner />;

  const save = async () => {
    if (!f.account_sid || !f.auth_token || !f.from_number) return toast.error("All fields are required");
    setSaving(true);
    try {
      const r = await api.post("/admin/sms-settings", f);
      setData(r.data); setF({ account_sid: "", auth_token: "", from_number: "" });
      toast.success("Twilio SMS configured");
    } catch { toast.error("Only Super Admin can save SMS settings"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="font-heading font-bold flex items-center gap-2"><MessageSquare size={18} className="text-primary" /> Twilio SMS</h3>
        <div className={`mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold ${data.configured ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15" : "bg-slate-100 text-slate-600 dark:bg-slate-500/15"}`} data-testid="sms-status">
          <span className={`w-1.5 h-1.5 rounded-full ${data.configured ? "bg-emerald-500" : "bg-slate-400"}`} /> {data.configured ? "Configured" : "Not configured"}
        </div>
        {data.configured && <p className="mt-3 text-sm text-muted-foreground">Sender: <b>{data.from_number}</b><br />Account: <span className="font-mono">{data.account_sid_masked}</span></p>}
        <p className="mt-4 text-xs text-muted-foreground">Payment reminder SMS (with a one-tap PayFast link) are sent automatically 1 day before and on the invoice due date. Add your Twilio credentials to switch this on.</p>
      </div>
      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="font-heading font-bold mb-4">{data.configured ? "Update credentials" : "Add credentials"}</h3>
        <div className="space-y-3">
          <div><Label>Account SID</Label><Input data-testid="sms-sid" value={f.account_sid} onChange={set("account_sid")} placeholder="ACxxxxxxxx" disabled={!isSuperAdmin} className="mt-1.5" /></div>
          <div><Label>Auth Token</Label><Input data-testid="sms-token" type="password" value={f.auth_token} onChange={set("auth_token")} placeholder="••••••••" disabled={!isSuperAdmin} className="mt-1.5" /></div>
          <div><Label>Twilio phone number</Label><Input data-testid="sms-from" value={f.from_number} onChange={set("from_number")} placeholder="+1..." disabled={!isSuperAdmin} className="mt-1.5" /></div>
          <Button onClick={save} disabled={saving || !isSuperAdmin} className="w-full bg-primary hover:bg-red-700" data-testid="sms-save">{saving ? "Saving…" : "Save SMS Settings"}</Button>
          {!isSuperAdmin && <p className="text-xs text-muted-foreground">Only the Super Administrator can change these.</p>}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- PayFast Settings ----------------------------- */
function PayfastSettings({ isSuperAdmin }) {
  const { data, loading, setData } = useFetch(() => api.get("/admin/payfast-settings").then((r) => r.data));
  const [f, setF] = useState({ merchant_id: "", merchant_key: "", passphrase: "", mode: "live" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  if (loading) return <Spinner />;

  const save = async () => {
    if (!f.merchant_id || !f.merchant_key) return toast.error("Merchant ID and Merchant Key are required");
    setSaving(true);
    try {
      const r = await api.post("/admin/payfast-settings", f);
      setData(r.data); setF({ merchant_id: "", merchant_key: "", passphrase: "", mode: "live" });
      toast.success(`PayFast configured (${r.data.mode})`);
    } catch (e) { toast.error(apiErr(e, "Only Super Admin can save PayFast settings")); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="font-heading font-bold flex items-center gap-2"><CreditCard size={18} className="text-primary" /> PayFast Gateway</h3>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold ${data.configured ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15" : "bg-amber-100 text-amber-700 dark:bg-amber-500/15"}`} data-testid="payfast-status">
            <span className={`w-1.5 h-1.5 rounded-full ${data.configured ? "bg-emerald-500" : "bg-amber-500"}`} /> {data.configured ? "Live merchant configured" : "Sandbox (test) mode"}
          </span>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${data.mode === "live" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15" : "bg-slate-100 text-slate-600 dark:bg-slate-500/15"}`}>{data.mode === "live" ? "LIVE" : "SANDBOX"}</span>
        </div>
        {data.configured && <p className="mt-3 text-sm text-muted-foreground">Merchant ID: <span className="font-mono">{data.merchant_id_masked}</span><br />Endpoint: <span className="font-mono">{data.host}</span></p>}
        <p className="mt-4 text-xs text-muted-foreground">Payments run on the shared PayFast <b>sandbox</b> until you enter your own merchant credentials here. Set the mode to <b>Live</b> to charge real customers. Get your Merchant ID / Key / Passphrase from your PayFast dashboard → Settings → Integration.</p>
      </div>
      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="font-heading font-bold mb-4">Merchant credentials</h3>
        <div className="space-y-3">
          <div><Label>Merchant ID</Label><Input data-testid="payfast-mid" value={f.merchant_id} onChange={set("merchant_id")} placeholder="e.g. 12345678" disabled={!isSuperAdmin} className="mt-1.5" /></div>
          <div><Label>Merchant Key</Label><Input data-testid="payfast-key" value={f.merchant_key} onChange={set("merchant_key")} placeholder="merchant key" disabled={!isSuperAdmin} className="mt-1.5" /></div>
          <div><Label>Passphrase</Label><Input data-testid="payfast-pass" type="password" value={f.passphrase} onChange={set("passphrase")} placeholder="••••••••" disabled={!isSuperAdmin} className="mt-1.5" /></div>
          <div>
            <Label>Mode</Label>
            <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })} disabled={!isSuperAdmin}>
              <SelectTrigger data-testid="payfast-mode" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="sandbox">Sandbox (test)</SelectItem><SelectItem value="live">Live (production)</SelectItem></SelectContent>
            </Select>
          </div>
          <Button onClick={save} disabled={saving || !isSuperAdmin} className="w-full bg-primary hover:bg-red-700" data-testid="payfast-save">{saving ? "Saving…" : "Save PayFast Settings"}</Button>
          {!isSuperAdmin && <p className="text-xs text-muted-foreground">Only the Super Administrator can change these.</p>}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Companies ----------------------------- */
const EMPTY_CO = { name: "", contact_email: "", phone: "", industry: "", address: "", credit_limit_zar: 0 };
function Companies({ canManage }) {
  const { data, loading, setData } = useFetch(() => api.get("/admin/companies").then((r) => r.data));
  const [form, setForm] = useState(EMPTY_CO);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  if (loading) return <Spinner />;
  const companies = data.companies || [];
  const reload = async () => setData((await api.get("/admin/companies")).data);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startEdit = (c) => { setEditId(c.id); setForm({ name: c.name || "", contact_email: c.contact_email || "", phone: c.phone || "", industry: c.industry || "", address: c.address || "", credit_limit_zar: c.credit_limit_zar || 0 }); };
  const cancel = () => { setEditId(null); setForm(EMPTY_CO); };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Company name is required");
    setBusy(true);
    try {
      if (editId) { await api.patch(`/admin/companies/${editId}`, form); toast.success("Company updated"); }
      else { await api.post("/admin/companies", form); toast.success("Company added"); }
      cancel(); await reload();
    } catch (e) { toast.error(apiErr(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/companies/${c.id}`); toast.success("Company deleted"); await reload(); }
    catch (e) { toast.error(apiErr(e, "Delete failed")); }
  };

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="company-form">
          <h3 className="font-heading font-bold text-sm flex items-center gap-2 mb-3">
            {editId ? <Pencil size={16} className="text-primary" /> : <Plus size={16} className="text-primary" />}
            {editId ? "Edit client company" : "Add a client company"}
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input placeholder="Company name" value={form.name} onChange={set("name")} data-testid="company-name" />
            <Input placeholder="Contact email (real client inbox)" value={form.contact_email} onChange={set("contact_email")} data-testid="company-email" />
            <Input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="company-phone" />
            <Input placeholder="Industry" value={form.industry} onChange={set("industry")} data-testid="company-industry" />
            <Input placeholder="Address" value={form.address} onChange={set("address")} data-testid="company-address" />
            <Input type="number" placeholder="Credit limit (ZAR)" value={form.credit_limit_zar} onChange={set("credit_limit_zar")} data-testid="company-credit" />
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={submit} disabled={busy} className="bg-primary hover:bg-red-700" data-testid="company-submit">{busy ? "Saving…" : editId ? "Update Company" : "Add Company"}</Button>
            {editId && <Button variant="outline" onClick={cancel} data-testid="company-cancel">Cancel</Button>}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Clearance & receipt emails are sent to each company's <b>contact email</b> — use a real client inbox for production.</p>
        </div>
      )}
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="p-4">Company</th><th className="p-4 hidden md:table-cell">Contact Email</th><th className="p-4 hidden lg:table-cell">Industry</th><th className="p-4 hidden lg:table-cell">Phone</th>{canManage && <th className="p-4 text-right">Actions</th>}</tr>
          </thead>
          <tbody className="divide-y divide-border">
            {companies.length === 0 && <tr><td colSpan={5} className="p-6 text-muted-foreground">No client companies yet. Add your first real client above.</td></tr>}
            {companies.map((c) => (
              <tr key={c.id} data-testid={`company-row-${c.id}`} className="hover:bg-muted/40">
                <td className="p-4 font-semibold">{c.name}</td>
                <td className="p-4 hidden md:table-cell text-muted-foreground">{c.contact_email || "—"}</td>
                <td className="p-4 hidden lg:table-cell text-muted-foreground">{c.industry || "—"}</td>
                <td className="p-4 hidden lg:table-cell text-muted-foreground">{c.phone || "—"}</td>
                {canManage && (
                  <td className="p-4 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => startEdit(c)} data-testid={`company-edit-${c.id}`}><Pencil size={16} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(c)} data-testid={`company-delete-${c.id}`} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 size={16} /></Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------------- Security ----------------------------- */
function Security({ isSuperAdmin }) {
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [reset, setReset] = useState("");
  const [resetting, setResetting] = useState(false);
  const set = (k) => (e) => setPw({ ...pw, [k]: e.target.value });

  const change = async () => {
    if (!pw.current_password || !pw.new_password) return toast.error("Fill in all fields");
    if (pw.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw.new_password !== pw.confirm) return toast.error("New passwords do not match");
    setSaving(true);
    try { await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password }); toast.success("Password changed"); setPw({ current_password: "", new_password: "", confirm: "" }); }
    catch (e) { toast.error(apiErr(e, "Could not change password")); }
    finally { setSaving(false); }
  };

  const runReset = async () => {
    if (reset !== "RESET") return toast.error('Type RESET to confirm');
    if (!window.confirm("This permanently deletes ALL demo customers, shipments, invoices, quotes and cross-border data. Internal staff accounts stay. Continue?")) return;
    setResetting(true);
    try { const r = await api.post("/admin/production-reset", { confirm: "RESET" }); toast.success("Demo data wiped — app is production-clean"); setReset(""); console.log("wiped", r.data.wiped); }
    catch (e) { toast.error(apiErr(e, "Reset failed")); }
    finally { setResetting(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="font-heading font-bold flex items-center gap-2 mb-4"><KeyRound size={18} className="text-primary" /> Change my password</h3>
        <div className="space-y-3">
          <div><Label>Current password</Label><Input data-testid="pw-current" type="password" value={pw.current_password} onChange={set("current_password")} className="mt-1.5" /></div>
          <div><Label>New password</Label><Input data-testid="pw-new" type="password" value={pw.new_password} onChange={set("new_password")} className="mt-1.5" /></div>
          <div><Label>Confirm new password</Label><Input data-testid="pw-confirm" type="password" value={pw.confirm} onChange={set("confirm")} className="mt-1.5" /></div>
          <Button onClick={change} disabled={saving} className="w-full bg-primary hover:bg-red-700" data-testid="pw-save">{saving ? "Saving…" : "Update Password"}</Button>
        </div>
      </div>
      {isSuperAdmin && (
        <div className="rounded-xl border border-red-300 dark:border-red-500/40 bg-red-50/50 dark:bg-red-500/5 p-6" data-testid="danger-zone">
          <h3 className="font-heading font-bold flex items-center gap-2 mb-2 text-red-700 dark:text-red-400"><AlertTriangle size={18} /> Go-Live: wipe demo data</h3>
          <p className="text-sm text-muted-foreground mb-4">Removes all seeded demo customers, shipments, invoices, quotes, cross-border records and saved locations so you launch on a clean slate. Your internal Ebik staff accounts, fleet, warehouse and settings are kept. This cannot be undone.</p>
          <Label>Type <span className="font-mono font-bold">RESET</span> to confirm</Label>
          <Input data-testid="reset-confirm" value={reset} onChange={(e) => setReset(e.target.value)} placeholder="RESET" className="mt-1.5 mb-3" />
          <Button onClick={runReset} disabled={resetting || reset !== "RESET"} variant="destructive" className="w-full" data-testid="reset-run">{resetting ? "Wiping…" : "Wipe Demo Data"}</Button>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- Main ----------------------------- */
export default function Admin() {
  const { user } = useAuth();
  const { data, loading, setData } = useFetch(() => api.get("/admin/users").then((r) => r.data));
  const logs = useFetch(() => api.get("/admin/audit-logs").then((r) => r.data));
  const canManage = ["super_admin", "managing_director"].includes(user.role);
  const isSuperAdmin = user.role === "super_admin";
  const [nu, setNu] = useState({ name: "", email: "", password: "", role: "customer", branch: "" });
  const [creating, setCreating] = useState(false);
  if (loading) return <Spinner />;

  const reload = async () => { const r = await api.get("/admin/users"); setData(r.data); };
  const changeRole = async (uid, role) => {
    try { await api.patch(`/admin/users/${uid}/role?role=${role}`); await reload(); toast.success("Role updated"); }
    catch { toast.error("Only Super Admin can change roles"); }
  };
  const createUser = async () => {
    if (!nu.name || !nu.email || !nu.password) return toast.error("Name, email and password are required");
    setCreating(true);
    try { await api.post("/admin/users", nu); toast.success("User created"); setNu({ name: "", email: "", password: "", role: "customer", branch: "" }); await reload(); }
    catch (e) { toast.error(apiErr(e, "Create failed")); }
    finally { setCreating(false); }
  };
  const deleteUser = async (uid, email) => {
    if (!window.confirm(`Delete ${email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${uid}`); toast.success("User deleted"); await reload(); }
    catch (e) { toast.error(apiErr(e, "Delete failed")); }
  };
  const resetPassword = async (uid, email) => {
    const np = window.prompt(`Set a new password for ${email} (min 8 characters):`);
    if (np == null) return;
    if (np.length < 8) return toast.error("Password must be at least 8 characters");
    try { await api.post(`/admin/users/${uid}/password`, { new_password: np }); toast.success("Password reset"); }
    catch (e) { toast.error(apiErr(e, "Reset failed")); }
  };

  return (
    <div>
      <PageHeader title="Administration" subtitle="Users, clients, payments, security & audit trail" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard label="Total Users" value={data.users.length} icon={Users} />
        <StatCard label="Roles" value={data.roles.length} icon={ShieldCheck} accent="text-blue-600" />
        <StatCard label="Audit Events" value={logs.data?.logs?.length || 0} icon={ScrollText} accent="text-emerald-600" />
      </div>

      <Tabs defaultValue="users">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users">Users & Roles</TabsTrigger>
          <TabsTrigger value="companies" data-testid="admin-tab-companies">Client Companies</TabsTrigger>
          <TabsTrigger value="payfast" data-testid="admin-tab-payfast">Payments</TabsTrigger>
          <TabsTrigger value="sms" data-testid="admin-tab-sms">SMS Settings</TabsTrigger>
          <TabsTrigger value="security" data-testid="admin-tab-security">Security</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Trail</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          {canManage && (
            <div className="rounded-xl border border-border bg-card p-4 mb-4" data-testid="admin-create-user">
              <h3 className="font-heading font-bold text-sm flex items-center gap-2 mb-3"><UserPlus size={16} className="text-primary" /> Add a user</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <Input placeholder="Full name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} data-testid="new-user-name" />
                <Input placeholder="Email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} data-testid="new-user-email" />
                <Input type="password" placeholder="Password" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} data-testid="new-user-password" />
                <Select value={nu.role} onValueChange={(v) => setNu({ ...nu, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{data.roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={createUser} disabled={creating} className="bg-primary hover:bg-red-700" data-testid="new-user-submit">{creating ? "Creating…" : "Create User"}</Button>
              </div>
            </div>
          )}
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="p-4">Name</th><th className="p-4 hidden md:table-cell">Email</th><th className="p-4 hidden lg:table-cell">Branch</th><th className="p-4">Role</th>{canManage && <th className="p-4 text-right">Actions</th>}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-${u.email}`} className="hover:bg-muted/40">
                    <td className="p-4 font-semibold">{u.name}</td>
                    <td className="p-4 hidden md:table-cell text-muted-foreground">{u.email}</td>
                    <td className="p-4 hidden lg:table-cell text-muted-foreground">{u.branch}</td>
                    <td className="p-4">
                      <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={!isSuperAdmin}>
                        <SelectTrigger className="w-[190px] h-9" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{data.roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    {canManage && (
                      <td className="p-4 text-right whitespace-nowrap">
                        {isSuperAdmin && (
                          <Button variant="ghost" size="icon" onClick={() => resetPassword(u.id, u.email)} data-testid={`reset-pw-${u.email}`} title="Reset password"><KeyRound size={16} /></Button>
                        )}
                        {u.id !== user.id && (
                          <Button variant="ghost" size="icon" onClick={() => deleteUser(u.id, u.email)} data-testid={`delete-user-${u.email}`} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 size={16} /></Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="companies"><Companies canManage={canManage} /></TabsContent>
        <TabsContent value="payfast"><PayfastSettings isSuperAdmin={isSuperAdmin} /></TabsContent>
        <TabsContent value="sms"><SmsSettings isSuperAdmin={isSuperAdmin} /></TabsContent>
        <TabsContent value="security"><Security isSuperAdmin={isSuperAdmin} /></TabsContent>

        <TabsContent value="audit">
          <div className="rounded-xl border border-border bg-card divide-y divide-border max-h-[600px] overflow-y-auto scrollbar-thin">
            {(logs.data?.logs || []).length === 0 && <p className="p-6 text-sm text-muted-foreground">No audit events yet.</p>}
            {(logs.data?.logs || []).map((l, i) => (
              <div key={i} className="p-4 flex items-center justify-between" data-testid="audit-log">
                <div>
                  <p className="text-sm"><b className="capitalize">{l.action}</b> {l.entity} <span className="font-mono text-muted-foreground">{l.detail}</span></p>
                  <p className="text-xs text-muted-foreground mt-0.5">{l.user_email} · {l.role}</p>
                </div>
                <span className="text-xs text-muted-foreground font-mono">{fmt(l.timestamp)}</span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
